/** Small presentational helpers shared by the console views. */

import { useState, type ReactNode } from 'react'

const STATE_CLASS: Record<string, string> = {
  live: 'live',
  starting: 'warn',
  reconnecting: 'warn',
  stopping: 'warn',
  idle: 'idle',
  offline: 'off',
  unknown: 'off'
}

export function StatePill({ state }: { state: string }): React.JSX.Element {
  return <span className={`pill ${STATE_CLASS[state] ?? 'off'}`}>{state}</span>
}

/**
 * "3m ago" / "in 12m". Coarse on purpose — the exact second is never what the
 * operator is asking, and a ticking clock in a table is just noise.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return '—'

  const deltaSec = Math.round((Date.now() - then) / 1000)
  const future = deltaSec < 0
  const abs = Math.abs(deltaSec)

  const text =
    abs < 10
      ? 'just now'
      : abs < 60
        ? `${abs}s`
        : abs < 3600
          ? `${Math.floor(abs / 60)}m`
          : abs < 86400
            ? `${Math.floor(abs / 3600)}h`
            : `${Math.floor(abs / 86400)}d`

  if (text === 'just now') return text
  return future ? `in ${text}` : `${text} ago`
}

export function formatTime(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

/* ---------- form controls (mirrors the desktop client's ui.tsx) ---------- */

/** Label + control + optional hint, as one labelled unit. */
export function Field({
  label,
  hint,
  children
}: {
  label?: string
  hint?: ReactNode
  children: ReactNode
}): React.JSX.Element {
  return (
    <div className="field">
      {label && <label>{label}</label>}
      {children}
      {hint && <p className="hint">{hint}</p>}
    </div>
  )
}

export function TextField({
  label,
  value,
  placeholder,
  hint,
  onChange
}: {
  label?: string
  value: string
  placeholder?: string
  hint?: ReactNode
  onChange: (value: string) => void
}): React.JSX.Element {
  return (
    <Field label={label} hint={hint}>
      <input
        value={value}
        spellCheck={false}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  )
}

export function NumberField({
  label,
  value,
  min = 0,
  hint,
  onChange
}: {
  label: string
  value: number
  min?: number
  hint?: ReactNode
  onChange: (value: number) => void
}): React.JSX.Element {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => {
          const parsed = Number(e.target.value)
          if (Number.isFinite(parsed) && parsed >= min) onChange(parsed)
        }}
      />
    </Field>
  )
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  hint,
  onChange
}: {
  label: string
  value: T
  options: readonly (readonly [T, string])[]
  hint?: ReactNode
  onChange: (value: T) => void
}): React.JSX.Element {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
    </Field>
  )
}

/**
 * A checkbox and its explanation as one clickable statement — the whole row is
 * the label, which is both a bigger target and easier to scan than a bare box.
 */
export function Check({
  checked,
  onChange,
  children
}: {
  checked: boolean
  onChange: (value: boolean) => void
  children: ReactNode
}): React.JSX.Element {
  return (
    <label className={`check ${checked ? 'on' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  )
}

/** Masked text input with a reveal toggle, for stream keys. */
export function SecretField({
  label,
  value,
  placeholder,
  hint,
  onChange
}: {
  label?: string
  value: string
  placeholder?: string
  hint?: ReactNode
  onChange: (value: string) => void
}): React.JSX.Element {
  const [shown, setShown] = useState(false)
  return (
    <Field label={label} hint={hint}>
      <div className="inline">
        <input
          type={shown ? 'text' : 'password'}
          value={value}
          spellCheck={false}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" className="small" onClick={() => setShown((v) => !v)}>
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
    </Field>
  )
}
